import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { paginationStorage } from '../storage/local/index';

/**
 * Page state for one table, persisted across remounts within a route (AppLayout clears every
 * scope on navigation).
 *
 * `scope` must be unique per table INSTANCE, not per table type — several instances of the same
 * table render at once (one per event row on Events, one per expanded row on the cap table), and
 * a shared scope makes them all request the same offset. Include the entity id for those.
 */
export const usePagination = (
  scope: string,
): [number, Dispatch<SetStateAction<number>>, () => void] => {
  const [currentPage, setPage] = useState(() => paginationStorage.get(scope) || 1);

  // Stable across renders: callers pass these into useEffect/useCallback dependency arrays,
  // where a fresh identity every render re-ran the effect (and kept resetting the page to 1).
  const handlePageChange: Dispatch<SetStateAction<number>> = useCallback(
    (value) => {
      setPage((prev) => {
        const nextPage = typeof value === 'function' ? value(prev) : value;
        paginationStorage.set(scope, nextPage);
        return nextPage;
      });
    },
    [scope],
  );

  const resetCounter = useCallback(() => {
    setPage(1);
    paginationStorage.set(scope, 1);
  }, [scope]);

  return [currentPage, handlePageChange, resetCounter];
};
